import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'

import { BulkUploadBanner } from '@/components/BulkUploadBanner'
import { useGlobalState } from '@/hooks/useGlobalState'
import { UploadPanel } from '@/screens/BulkContentUpload/UploadPanel'
import { BULK_UPLOAD_RECORD_PREFIX } from '@/services/state/BulkUploadStore'

const OTHER_TAB_RECHECK_MS = 5000

/** The bulk upload's banner and panel, mounted above the routes so both outlive navigation (decision 6). */
export const BulkUploadHost = observer(function BulkUploadHost() {
  const { bulkUploadStore: store, authenticationStore } = useGlobalState()
  const sponsorId = authenticationStore.user?.sponsorId

  useEffect(() => {
    if (authenticationStore.ready && sponsorId) void store.restore()
  }, [store, authenticationStore.ready, sponsorId])

  // Decision 50: the browser's own prompt is all a close or refresh allows.
  useEffect(() => {
    if (!store.isBusy) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!store.leaveConfirmed) e.preventDefault()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [store, store.isBusy])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key?.startsWith(BULK_UPLOAD_RECORD_PREFIX)) void store.syncFromOtherTab(e.key)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [store])

  // A tab that crashes writes nothing on its way out, so its lock is the only sign it's gone. The
  // same wait covers this tab's own reload, when the page it replaced can still hold the lock.
  const waiting = store.otherTabBusy || store.restorePending
  useEffect(() => {
    if (!waiting) return
    const id = setInterval(() => void store.restore(), OTHER_TAB_RECHECK_MS)
    return () => clearInterval(id)
  }, [store, waiting])

  return (
    <>
      <BulkUploadBanner />
      <UploadPanel />
    </>
  )
})
